import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchCategoryDropdown } from '../../../../slice/category/categorySlice';
import Header from '../../Header';
import Footer from '../../components/Footer';
import { toast } from "react-toastify";
import { updateProfile } from "../../../../slice/user/userSlice";

/* ─── icons ──────────────────────────────────────────── */
const IconPin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
    <circle cx="12" cy="9" r="2.5"/>
  </svg>
);
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);
const IconEdit = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconPhone = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ProfileOverview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dropdownCategories = [] } = useSelector((state) => state.category);
  const { user, loading: authLoading } = useSelector((s) => s.auth);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phoneNumber: "" });
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(fetchCategoryDropdown());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        phoneNumber: user.phoneNumber || "",
      });
    }
  }, [user]);

  const navItems = [
    { label: "Profile Overview", icon: <IconUser />, path: "/profile" },
    { label: "Saved Addresses", icon: <IconPin />, path: "/profile/address" },
  ];

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await dispatch(updateProfile({ name: form.name, email: form.email })).unwrap();
      toast.success("Profile updated successfully");
      setEditMode(false);
    } catch (err) {
      toast.error(err || "Failed to update profile");
    }
  };

  const S = {
    layout: {
      maxWidth: "1100px",
      margin: "0 auto",
      padding: isMobile ? "20px 16px 100px" : "40px 24px 80px",
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? "16px" : "24px",
      alignItems: "flex-start",
    },
    sidebar: {
      width: isMobile ? "100%" : "260px",
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    sidebarTop: {
      background: "#fff",
      borderRadius: "16px",
      padding: isMobile ? "20px 16px" : "28px 20px 24px",
      textAlign: "center",
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    avatarCircle: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      background: "#fff3ea",
      border: "2px solid #fed7aa",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 14px",
      color: "#f97316",
    },
    sidebarName: { fontSize: "15px", fontWeight: 700, color: "#111", marginBottom: "4px" },
    sidebarSub: { fontSize: "12px", color: "#888" },
    sidebarNav: {
      background: "#fff",
      borderRadius: "16px",
      padding: "8px 0",
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      overflow: "hidden",
      display: isMobile ? "flex" : "block",
    },
    navItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      color: "#444",
      fontWeight: 500,
      transition: "background 0.15s, color 0.15s",
      flex: isMobile ? 1 : "unset",
      justifyContent: isMobile ? "center" : "flex-start",
    },
    navItemActive: {
      background: "#fff3ea",
      color: "#f97316",
      fontWeight: 600,
      borderLeft: isMobile ? "none" : "3px solid #f97316",
      borderBottom: isMobile ? "3px solid #f97316" : "none",
    },
    navItemIcon: { display: "flex", alignItems: "center", flexShrink: 0 },
    right: { flex: 1, minWidth: 0, width: isMobile ? "100%" : "auto" },
    topRow: {
      display: "flex",
      alignItems: isMobile ? "flex-start" : "center",
      justifyContent: "space-between",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "12px",
    },
    pageTitle: { fontSize: isMobile ? "18px" : "22px", fontWeight: 700, color: "#111", margin: 0 },
    pageSubtitle: { fontSize: "13px", color: "#888", marginTop: "3px" },
    profileCard: {
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "16px",
      padding: isMobile ? "20px 16px" : "32px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    avatarSection: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "24px",
      paddingBottom: "20px",
      borderBottom: "1px solid #f0f0f0",
      flexWrap: isMobile ? "wrap" : "nowrap",
    },
    avatarLarge: {
      width: "72px",
      height: "72px",
      borderRadius: "50%",
      background: "#fff3ea",
      border: "2px solid #fed7aa",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#f97316",
      flexShrink: 0,
    },
    avatarInfo: { flex: 1 },
    avatarName: { fontSize: isMobile ? "17px" : "20px", fontWeight: 700, color: "#111", marginBottom: "4px" },
    avatarRole: { fontSize: "13px", color: "#888" },
    memberBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      background: "#fff3ea",
      color: "#ea580c",
      fontSize: "11px",
      fontWeight: 600,
      padding: "4px 10px",
      borderRadius: "20px",
      marginTop: "6px",
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "16px",
    },
    infoItem: { display: "flex", flexDirection: "column", gap: "6px" },
    infoItemFull: { display: "flex", flexDirection: "column", gap: "6px", gridColumn: isMobile ? "auto" : "1 / -1" },
    infoLabel: {
      display: "flex",
      alignItems: "center",
      gap: "7px",
      fontSize: "12px",
      fontWeight: 600,
      color: "#888",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    infoValue: {
      fontSize: "14px",
      color: "#111",
      fontWeight: 500,
      padding: "10px 13px",
      background: "#f9fafb",
      borderRadius: "10px",
      border: "1px solid #f0f0f0",
    },
    infoEmpty: {
      fontSize: "14px",
      color: "#aaa",
      fontStyle: "italic",
      padding: "10px 13px",
      background: "#f9fafb",
      borderRadius: "10px",
      border: "1px solid #f0f0f0",
    },
    label: { display: "block", fontSize: "12px", fontWeight: 600, color: "#555", marginBottom: "6px" },
    input: {
      width: "100%",
      padding: "10px 13px",
      border: "1.5px solid #e5e7eb",
      borderRadius: "10px",
      fontSize: "13px",
      color: "#111",
      outline: "none",
      background: "#fff",
      fontFamily: "inherit",
      transition: "border-color 0.2s",
      boxSizing: "border-box",
    },
    inputDisabled: {
      width: "100%",
      padding: "10px 13px",
      border: "1.5px solid #e5e7eb",
      borderRadius: "10px",
      fontSize: "13px",
      color: "#9ca3af",
      outline: "none",
      background: "#f3f4f6",
      fontFamily: "inherit",
      boxSizing: "border-box",
      cursor: "not-allowed",
    },
    inputErr: { borderColor: "#f87171" },
    errText: { fontSize: "11px", color: "#dc2626", marginTop: "4px" },
    btnEdit: {
      display: "inline-flex",
      alignItems: "center",
      gap: "7px",
      background: "#f97316",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      padding: "10px 20px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
    },
    btnSave: {
      background: "#f97316",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      padding: "10px 28px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      flex: isMobile ? 1 : "unset",
    },
    btnCancel: {
      background: "none",
      border: "1.5px solid #e5e7eb",
      borderRadius: "10px",
      padding: "10px 22px",
      fontSize: "13px",
      fontWeight: 600,
      color: "#555",
      cursor: "pointer",
      flex: isMobile ? 1 : "unset",
    },
    actionRow: {
      display: "flex",
      gap: "10px",
      justifyContent: isMobile ? "stretch" : "flex-end",
      marginTop: "24px",
    },
    editGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "16px",
    },
    hintText: { fontSize: "11px", color: "#aaa", marginTop: "4px" },
  };

  return (
    <div className="page-wraper">
      <Header dropdownCategories={dropdownCategories} loading={authLoading} />
      <div className="content-inner-1 bg-light">
        <div className="container">
          <div className="row">
            <div style={S.layout}>

              {/* ══ LEFT SIDEBAR ══ */}
              <aside style={S.sidebar}>
                <div style={S.sidebarTop}>
                  <div style={S.avatarCircle}>
                    <IconUser />
                  </div>
                  <p style={S.sidebarName}>{user?.name || "User"}</p>
                  <p style={S.sidebarSub}>Profile Overview</p>
                  <p style={{ ...S.sidebarSub, marginTop: "2px" }}>Manage your account details</p>
                </div>

                <nav style={S.sidebarNav}>
                  {navItems.map((item) => {
                    const isActive = item.path === "/profile";
                    return (
                      <div
                        key={item.label}
                        style={{
                          ...S.navItem,
                          ...(isActive ? S.navItemActive : {}),
                          paddingLeft: isActive && !isMobile ? "17px" : isMobile ? "12px" : "20px",
                        }}
                        onClick={() => navigate(item.path)}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "#f9fafb"; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                      >
                        <span style={{ ...S.navItemIcon, color: isActive ? "#f97316" : "#888" }}>
                          {item.icon}
                        </span>
                        {item.label}
                      </div>
                    );
                  })}
                </nav>
              </aside>

              {/* ══ RIGHT CONTENT ══ */}
              <div style={S.right}>
                <div style={S.topRow}>
                  <div>
                    <h1 style={S.pageTitle}>Profile Overview</h1>
                    <p style={S.pageSubtitle}>View and manage your personal information</p>
                  </div>
                  {!editMode && (
                    <button
                      style={S.btnEdit}
                      onClick={() => setEditMode(true)}
                      onMouseEnter={e => e.currentTarget.style.background = "#ea580c"}
                      onMouseLeave={e => e.currentTarget.style.background = "#f97316"}
                    >
                      <IconEdit /> Edit Profile
                    </button>
                  )}
                </div>

                <div style={S.profileCard}>

                  {/* ── Avatar row ── */}
                  <div style={S.avatarSection}>
                    <div style={S.avatarLarge}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <div style={S.avatarInfo}>
                      <p style={S.avatarName}>{user?.name || "User"}</p>
                      <p style={S.avatarRole}>{user?.email || ""}</p>
                      <div style={S.memberBadge}>
                        <IconCheck /> Active Member
                      </div>
                    </div>
                  </div>

                  {/* ══ VIEW MODE ══ */}
                  {!editMode && (
                    <div style={S.infoGrid}>

                      <div style={S.infoItem}>
                        <span style={S.infoLabel}><IconUser /> Full Name</span>
                        {user?.name
                          ? <span style={S.infoValue}>{user.name}</span>
                          : <span style={S.infoEmpty}>Not provided</span>
                        }
                      </div>

                      <div style={S.infoItem}>
                        <span style={S.infoLabel}><IconPhone /> Phone Number</span>
                        {user?.phoneNumber
                          ? <span style={S.infoValue}>{user.phoneNumber}</span>
                          : <span style={S.infoEmpty}>Not provided</span>
                        }
                      </div>

                      <div style={S.infoItemFull}>
                        <span style={S.infoLabel}><IconMail /> Email Address</span>
                        {user?.email
                          ? <span style={S.infoValue}>{user.email}</span>
                          : <span style={S.infoEmpty}>Not provided</span>
                        }
                      </div>

                    </div>
                  )}

                  {/* ══ EDIT MODE ══ */}
                  {editMode && (
                    <>
                      <div style={S.editGrid}>

                        {/* Full Name */}
                        <div>
                          <label style={S.label}>
                            Full Name <span style={{ color: "#f97316" }}>*</span>
                          </label>
                          <input
                            name="name"
                            placeholder="Your full name"
                            value={form.name}
                            onChange={handleChange}
                            style={{ ...S.input, ...(errors.name ? S.inputErr : {}) }}
                          />
                          {errors.name && <p style={S.errText}>{errors.name}</p>}
                        </div>

                        {/* Phone — read only */}
                        <div>
                          <label style={S.label}>Phone Number</label>
                          <input
                            name="phoneNumber"
                            value={form.phoneNumber}
                            type="tel"
                            readOnly
                            style={S.inputDisabled}
                          />
                          <p style={S.hintText}>📵 Phone number cannot be changed</p>
                        </div>

                        {/* Email — full width */}
                        <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
                          <label style={S.label}>
                            Email Address <span style={{ color: "#f97316" }}>*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            placeholder="you@example.com"
                            value={form.email}
                            onChange={handleChange}
                            style={{ ...S.input, ...(errors.email ? S.inputErr : {}) }}
                          />
                          {errors.email && <p style={S.errText}>{errors.email}</p>}
                        </div>

                      </div>

                      <div style={S.actionRow}>
                        <button
                          style={S.btnCancel}
                          onClick={() => { setEditMode(false); setErrors({}); }}
                        >
                          Cancel
                        </button>
                        <button
                          style={S.btnSave}
                          onClick={handleSave}
                          onMouseEnter={e => e.currentTarget.style.background = "#ea580c"}
                          onMouseLeave={e => e.currentTarget.style.background = "#f97316"}
                        >
                          Save Changes
                        </button>
                      </div>
                    </>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileOverview;